import { useState, useMemo } from 'react';
import { Copy, Download } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard, formatBytes, download } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE = `<?xml version="1.0" encoding="UTF-8"?>
<!-- Generated by some editor -->
<svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
     viewBox="0 0 100 100"
     style="enable-background:new 0 0 100 100;">
  <g id="Layer_1">
    <circle cx="50" cy="50" r="40" stroke-width="2" stroke="#6366f1" fill="rgba(99, 102, 241, 0.2)"/>
    <path d="M 30,50 L 50,30 L 70,50 L 50,70 Z" fill="#a78bfa" />
  </g>
</svg>`;

interface Opts {
  removeComments: boolean;
  removeXmlDecl: boolean;
  removeMeta: boolean;
  removeEmptyGroups: boolean;
  collapseWhitespace: boolean;
  roundNumbers: boolean;
  removeDefaultAttrs: boolean;
  shortHex: boolean;
}

const DEFAULTS: Opts = {
  removeComments: true,
  removeXmlDecl: true,
  removeMeta: true,
  removeEmptyGroups: true,
  collapseWhitespace: true,
  roundNumbers: true,
  removeDefaultAttrs: true,
  shortHex: true,
};

const DEFAULT_ATTRS: Record<string, Record<string, string>> = {
  '*': { 'xml:space': 'default', 'enable-background': 'new', version: '1.1' },
  rect: { rx: '0', ry: '0', x: '0', y: '0' },
  circle: { 'stroke-width': '1' },
};

function shortenHex(s: string): string {
  return s.replace(/#([0-9a-fA-F])\1([0-9a-fA-F])\2([0-9a-fA-F])\3\b/g, '#$1$2$3');
}

function rgbaToHex(s: string): string {
  return s.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/g, (_, r, g, b, a) => {
    if (a !== undefined && parseFloat(a) < 1) return _;
    const h = (n: string) => parseInt(n).toString(16).padStart(2, '0');
    return '#' + h(r) + h(g) + h(b);
  });
}

function roundNums(s: string): string {
  return s.replace(/-?\d+\.\d{4,}/g, (m) => parseFloat(m).toFixed(3).replace(/\.?0+$/, ''));
}

function removeAttr(s: string, tag: string, attr: string, defaultValue: string): string {
  const re = new RegExp(`(<${tag}[^>]*)\\s+${attr}=["']${defaultValue}["']`, 'g');
  return s.replace(re, '$1');
}

function optimize(input: string, opts: Opts): string {
  let s = input;
  if (opts.removeXmlDecl) s = s.replace(/<\?xml[^?]*\?>\s*/g, '');
  if (opts.removeComments) s = s.replace(/<!--[\s\S]*?-->\s*/g, '');
  if (opts.removeMeta) s = s.replace(/<(metadata|sodipodi:[^>]+|inkscape:[^>]+)[\s\S]*?<\/\1>/g, '');
  if (opts.removeEmptyGroups) {
    let prev = '';
    while (prev !== s) {
      prev = s;
      s = s.replace(/<g[^>]*>\s*<\/g>/g, '');
    }
  }
  if (opts.removeDefaultAttrs) {
    Object.entries(DEFAULT_ATTRS['*'] ?? {}).forEach(([k, v]) => {
      s = removeAttr(s, '\\w+', k, v);
    });
  }
  if (opts.shortHex) {
    s = rgbaToHex(s);
    s = shortenHex(s);
  }
  if (opts.roundNumbers) s = roundNums(s);
  if (opts.collapseWhitespace) {
    s = s.replace(/>\s+</g, '><').replace(/\s+/g, ' ').trim();
  }
  return s;
}

export function SvgOptimizer() {
  const [input, setInput] = useState(SAMPLE);
  const [opts, setOpts] = useState<Opts>(DEFAULTS);

  const output = useMemo(() => optimize(input, opts), [input, opts]);

  const inputBytes = new Blob([input]).size;
  const outputBytes = new Blob([output]).size;
  const saved = inputBytes - outputBytes;

  return (
    <div>
      <ToolHeader
        toolId="svg-optimizer"
        title="SVG optimizer"
        description="Strip comments, default attributes, editor metadata, and whitespace. Shorten hex codes and round long decimals."
        badges={
          <Badge variant={saved > 0 ? 'success' : 'warning'}>
            {saved > 0 ? `−${formatBytes(saved)}` : 'no savings'}
          </Badge>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-2.5">
          <div className="text-xs font-medium text-fg-muted mb-1">Optimizations</div>
          {([
            ['removeComments', 'Strip comments'],
            ['removeXmlDecl', 'Strip <?xml ?> decl'],
            ['removeMeta', 'Strip editor metadata'],
            ['removeEmptyGroups', 'Drop empty groups'],
            ['removeDefaultAttrs', 'Remove default attrs'],
            ['shortHex', 'Shorten hex colors'],
            ['roundNumbers', 'Round long decimals'],
            ['collapseWhitespace', 'Collapse whitespace'],
          ] as const).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between text-sm cursor-pointer">
              <span className="text-fg-muted">{label}</span>
              <Switch
                checked={opts[key]}
                onChange={(v) => setOpts({ ...opts, [key]: v })}
              />
            </label>
          ))}
          <div className="border-t border-border pt-3 text-xs space-y-1.5 font-mono">
            <Row label="Input" value={formatBytes(inputBytes)} />
            <Row label="Output" value={formatBytes(outputBytes)} />
            <Row label="Saved" value={`${saved > 0 ? '−' : ''}${formatBytes(Math.abs(saved))}`} accent />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                const ok = await copyToClipboard(output);
                if (ok) toast.success('Copied');
              }}
            >
              <Copy size={11} />
              Copy
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => download(output, 'optimized.svg', 'image/svg+xml')}
            >
              <Download size={11} />
              Save
            </Button>
          </div>
        </Card>

        <div className="lg:col-span-2 lg:order-1 space-y-3">
          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Source SVG
            </div>
            <Textarea
              mono
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[180px] !bg-transparent focus-visible:!ring-0"
            />
          </Card>
          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Optimized
            </div>
            <pre className="font-mono text-[12px] leading-relaxed p-4 overflow-auto min-h-[120px] max-h-[260px] whitespace-pre-wrap break-all">
              <code>{output}</code>
            </pre>
          </Card>
          <Card>
            <div className="text-xs font-medium text-fg-muted mb-2">Visual</div>
            <div
              className="bg-bg-inset rounded-lg p-6 flex items-center justify-center min-h-[160px]"
              dangerouslySetInnerHTML={{ __html: output }}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-subtle">{label}</span>
      <span className={accent ? 'text-success font-semibold' : ''}>{value}</span>
    </div>
  );
}

export default SvgOptimizer;
